import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';

// Company-page step 5 (License Preferences), part 3 of 4 (22b-4d step 6a3):
// the "Job Boards" (Daxtra DB name, resumes-to-pull count) and its
// "Credentials" (job-board credentials CRUD table) sections of the old
// data-step="5" pane, previously inside the shared
// `form.companyAssessmentFlowDetailsForm` (company-page.html:418-750 before
// this sub-step). That shared wrapper form is retired with this sub-step -
// daxtraDbName was its only validated field, and its validity now lives in
// this component's own form-state emission instead, combined into the root
// controller's `this.valid` the same way as 6a1's licenseScoringFormValid
// and 6a2's assessmentsFormValid (see onJobBoardsFormStateChange).
//
// All business logic (showAddJobBoardKeyForm, addJobBoardKey,
// deleteJobBoardDetails, and every other credential-CRUD method) stays on
// the root legacy controller unchanged - this component only renders bound
// state (preferences, newJobBoardCredential, etc. by reference) and forwards
// click actions via @Output emitters, same pattern as
// company-page-step-license-assessments.
@Component({
  selector: 'app-company-page-step-license-jobboards',
  templateUrl: './company-page-step-license-jobboards.component.html'
})
export class CompanyPageStepLicenseJobboardsComponent implements AfterViewInit, OnDestroy {

  MESSAGECONSTANTS = MESSAGECONSTANTS;

  @Input() typeOfCompany: string;
  @Input() preferences: any = {};
  @Input() clientOrBuCreationInProgress: boolean;
  @Input() clientOrBuUpdateInProgress: boolean;
  @Input() isUserSuperUserOr4dot5Admin: boolean;
  @Input() isCompanyVendor: boolean;
  @Input() readOnlyForClientOrBU: boolean;
  @Input() isActivePlanExists: boolean;
  @Input() savedCurrentActivePlanName: string;
  @Input() companyName: string;
  @Input() valid: boolean;
  @Input() hasUnsavedJobBoardCredentials: boolean;
  @Input() showAddJobBoardCredentialsFormFlag: boolean;
  @Input() jobSourceType: any[] = [];
  @Input() newJobBoardCredential: any = {};
  @Input() resumePullCounts: number[] = [];

  @Output() showAddJobBoardKeyFormCallback = new EventEmitter<void>();
  @Output() showEditJobBoardKeyFormCallback = new EventEmitter<any>();
  @Output() updateEditJobBoardKeyFormCallback = new EventEmitter<{ jobBoardCredential: any, index: number }>();
  @Output() cancelEditJobBoardKeyFormCallback = new EventEmitter<number>();
  @Output() cancelDeleteJobBoardKeyCallback = new EventEmitter<any>();
  @Output() deleteJobBoardDetailsCallback = new EventEmitter<any>();
  @Output() jobBoardCredentialsAddSourceTypeChangedCallback = new EventEmitter<number>();
  @Output() jobBoardCredentialsEditSourceTypeChangedCallback = new EventEmitter<any>();
  @Output() addJobBoardKeyCallback = new EventEmitter<void>();
  @Output() cancelAddJobBoardKeyCallback = new EventEmitter<void>();
  @Output() formStateChange = new EventEmitter<any>();

  @ViewChild('jobBoardsCredentialsForm') jobBoardsCredentialsForm: NgForm;

  private subs: Subscription[] = [];

  ngAfterViewInit() {
    setTimeout(() => this.emitFormState());
    if (this.jobBoardsCredentialsForm) {
      this.subs.push(this.jobBoardsCredentialsForm.statusChanges.subscribe(() => this.emitFormState()));
    }
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  trackByIndex(index: number) {
    return index;
  }

  showAddJobBoardKeyForm() {
    this.showAddJobBoardKeyFormCallback.emit();
  }

  showEditJobBoardKeyForm(jobBoardCredential: any) {
    this.showEditJobBoardKeyFormCallback.emit(jobBoardCredential);
  }

  updateEditJobBoardKeyForm(jobBoardCredential: any, index: number) {
    this.updateEditJobBoardKeyFormCallback.emit({ jobBoardCredential, index });
  }

  cancelEditJobBoardKeyForm(index: number) {
    this.cancelEditJobBoardKeyFormCallback.emit(index);
  }

  cancelDeleteJobBoardKey(jobBoardCredential: any) {
    this.cancelDeleteJobBoardKeyCallback.emit(jobBoardCredential);
  }

  deleteJobBoardDetails(jobBoardCredential: any) {
    this.deleteJobBoardDetailsCallback.emit(jobBoardCredential);
  }

  jobBoardCredentialsAddSourceTypeChanged(index: number) {
    this.jobBoardCredentialsAddSourceTypeChangedCallback.emit(index);
  }

  jobBoardCredentialsEditSourceTypeChanged(jobBoardCredential: any) {
    this.jobBoardCredentialsEditSourceTypeChangedCallback.emit(jobBoardCredential);
  }

  addJobBoardKey() {
    this.addJobBoardKeyCallback.emit();
  }

  cancelAddJobBoardKey() {
    this.cancelAddJobBoardKeyCallback.emit();
  }

  private emitFormState() {
    const form = this.jobBoardsCredentialsForm;
    this.formStateChange.emit({
      $valid: form ? form.valid : true,
      $dirty: form ? form.dirty : false,
      $error: { required: form ? form.invalid : false },
      $setDirty: () => form && form.form.markAsDirty(),
      $setPristine: () => form && form.form.markAsPristine()
    });
  }
}
